<template>
  <div class="w-full h-full flex justify-center items-center">
    <div
      v-if="isSubscribed"
      class="chart-wrapper"
      :style="{
        position: 'relative',
        width: `${width}px`,
        height: `${height}px`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
        borderRadius: 'var(--radius-md, 8px)',
        backgroundColor,
        overflow: 'hidden'
      }"
    >
      <div
        v-if="bgImageBase64"
        class="chart-bg-image"
        :style="{...bgImageStyle, borderRadius: 'inherit'}"
      ></div>
      <div
        ref="chart"
        class="chart-container"
        :style="{ width: '100%', height: '100%' }"
      ></div>
    </div>

    <!-- Carousel for non-subscribed users -->
    <el-carousel
      v-else
      :interval="5000"
      arrow="hover"
      indicator-position="none"
      class="chart-carousel"
      trigger="click"
      :style="{
        width: '100%',
        height: '100%',
        borderRadius: 'var(--radius-md, 8px)',
        overflow: 'hidden'
      }"
    >
      <el-carousel-item v-for="(image, index) in carouselImages" :key="index">
        <img :src="require(`@/assets/${image}`)" :alt="image" class="w-full h-full object-cover" loading="lazy">
      </el-carousel-item>
    </el-carousel>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import 'echarts-wordcloud';
import { ElCarousel, ElCarouselItem, ElNotification } from 'element-plus';
import { debounce } from 'lodash-es';
import html2canvas from 'html2canvas';
import FontFaceObserver from 'fontfaceobserver';
import { createColorFunction } from '@/utils/colorPalettes';
import { encode as encodeGif } from 'modern-gif';

export default {
  name: 'WordCloudChart',
  components: {
    ElCarousel,
    ElCarouselItem
  },
  props: {
    isSubscribed: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    chartData: {
      type: Array,
      default: () => []
    },
    maskSrc: {
      type: String,
      default: ''
    },
    font: {
      type: String,
      default: 'Roboto'
    },
    backgroundColor: {
      type: String,
      default: '#FFFFFF'
    },
    width: {
      type: Number,
      default: 500
    },
    height: {
      type: Number,
      default: 500
    },
    minSize: {
      type: Number,
      default: 10
    },
    maxSize: {
      type: Number,
      default: 180
    },
    carouselImages: {
      type: Array,
      default: () => []
    },
    window: {
      type: Object,
      default: () => window
    },
    // Background image props
    bgImageBase64: {
      type: String,
      default: ''
    },
    bgImageOpacity: {
      type: Number,
      default: 0.3
    },
    bgImageBlur: {
      type: Number,
      default: 0
    },
    bgImageFitMode: {
      type: String,
      default: 'cover'
    },
    // Color palette prop
    colorPalette: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      chartInstance: null,
      debouncedUpdate: null,
      isRecording: false
    }
  },
  computed: {
    bgImageStyle() {
      const fitValue = this.bgImageFitMode === 'stretch' ? '100% 100%' : this.bgImageFitMode;
      const repeatValue = this.bgImageFitMode === 'repeat' ? 'repeat' : 'no-repeat';
      return {
        backgroundImage: `url(${this.bgImageBase64})`,
        backgroundSize: fitValue,
        backgroundRepeat: repeatValue,
        opacity: this.bgImageOpacity,
        filter: this.bgImageBlur > 0 ? `blur(${this.bgImageBlur}px)` : 'none'
      };
    }
  },
  mounted() {
    this.debouncedUpdate = debounce(this.updateChart, 500);

    if (this.isSubscribed) {
      this.initChart();
    }
  },
  watch: {
    chartData: {
      deep: true,
      handler() { this.debouncedUpdate(); }
    },
    maskSrc() { this.debouncedUpdate(); },
    font() { this.debouncedUpdate(); },
    backgroundColor() {},
    width() { this.debouncedUpdate(); },
    height() { this.debouncedUpdate(); },
    minSize() { this.debouncedUpdate(); },
    maxSize() { this.debouncedUpdate(); },
    colorPalette() { this.debouncedUpdate(); },
    isSubscribed(val) {
      if (val) {
        this.$nextTick(this.initChart);
      } else {
        if (this.chartInstance) {
          this.chartInstance.dispose();
          this.chartInstance = null;
        }
      }
    }
  },
  beforeUnmount() {
    if (this.debouncedUpdate) this.debouncedUpdate.cancel();
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
  },
  methods: {
    resize() {
      if (this.chartInstance) {
        this.chartInstance.resize();
      }
    },
    generateChartOption(maskImage = null) {
      // Use color palette if provided, otherwise random
      const paletteColors = this.colorPalette ? this.colorPalette.colors : null;
      const colorFn = createColorFunction(paletteColors);

      const option = {
        series: [{
          type: 'wordCloud',
          sizeRange: [this.minSize, this.maxSize],
          rotationRange: [-90, 90],
          gridSize: 1,
          drawOutOfBound: false,
          shrinkToFit: true,
          keepAspect: false,
          layoutAnimation: true,
          width: '100%',
          height: '100%',
          textStyle: {
            fontFamily: `'${this.font}'`,
            color: colorFn
          },
          data: this.chartData
        }]
      };

      if (maskImage) {
        option.series[0].maskImage = maskImage;
      }

      return option;
    },
    initChart() {
      if (!this.$refs.chart) return;

      const canvas = document.createElement('canvas');
      canvas.getContext('2d', { willReadFrequently: true });

      this.chartInstance = echarts.init(this.$refs.chart, null, {
        renderer: 'canvas'
      });

      this.updateChart();
    },
    async updateChart() {
      if (!this.isSubscribed || !this.$refs.chart) return;
      if (!this.chartInstance) this.initChart();

      try {
        // Wait for font to load
        if (this.font !== 'sans-serif' && this.font !== 'serif') {
           const fontObserver = new FontFaceObserver(this.font);
           try {
             await fontObserver.load(null, 5000);
           } catch (e) {
             console.warn(`Font ${this.font} failed to load or timed out. Rendering anyway.`);
           }
        }

        const handleMaskImage = (maskImage) => {
          const option = this.generateChartOption(maskImage);
          this.chartInstance.setOption(option);
          this.chartInstance.resize();
        };

        const maskImage = new Image();
        maskImage.crossOrigin = 'Anonymous';
        maskImage.onload = () => handleMaskImage(maskImage);
        maskImage.onerror = () => {
          console.warn('Failed to load mask image, using default options (no mask)');
          const option = this.generateChartOption();
          this.chartInstance.setOption(option);
          this.chartInstance.resize();
        };

        if (this.maskSrc) {
            maskImage.src = this.maskSrc;
        } else {
             handleMaskImage(null);
        }

      } catch (error) {
        console.error('Error updating chart:', error);
        ElNotification({
            title: this.$t('Error'),
            message: error.message || this.$t('Failed to update chart'),
            type: 'error'
        });
      }
    },
    async exportChart() {
        if (!this.$refs.chart) return;

        // Capture the wrapper so background color + bg image are included
        const wrapper = this.$refs.chart.parentElement;
        wrapper.classList.add('export-pure');

        try {
            const canvas = await html2canvas(wrapper, {
              backgroundColor: null,          // let the wrapper's CSS background show through
              logging: false,
              scale: 2
            });

            wrapper.classList.remove('export-pure');

            const dataURL = canvas.toDataURL('image/png');
            const a = document.createElement('a');
            a.href = dataURL;
            const date = new Date();
            const dateString = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
            const timestamp = Date.now();
            a.download = `wordcloudstudio_${dateString}_${timestamp}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

        } catch (e) {
            console.error(e);
            ElNotification({
                title: this.$t('Export Failed'),
                message: e.message,
                type: 'error'
            });
            wrapper.classList.remove('export-pure');
        }
    },

    // ── Video Recording (MP4 via h264-mp4-encoder) ──────────

    /**
     * Load the h264-mp4-encoder script dynamically (sets window.HME).
     * Returns a promise that resolves once the script is ready.
     */
    _loadHME() {
      if (window.HME) return Promise.resolve(window.HME);

      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = '/js/h264-mp4-encoder.web.js';
        script.onload = () => resolve(window.HME);
        script.onerror = () => reject(new Error('Failed to load video encoder'));
        document.head.appendChild(script);
      });
    },

    /**
     * Record the ECharts canvas as an MP4 video.
     * Captures frames during layout animation, encodes as H.264 MP4.
     * @param {number} durationMs - recording duration in ms
     * @param {number} fps - frames per second
     * @returns {Promise<Blob>} MP4 video blob
     */
    async recordVideo(durationMs = 4000, fps = 12) {
      if (!this.$refs.chart || !this.chartInstance) {
        throw new Error('Chart not initialized');
      }

      const echartsCanvas = this.$refs.chart.querySelector('canvas');
      if (!echartsCanvas) {
        throw new Error('ECharts canvas not found');
      }

      // Load the encoder (sets window.HME)
      const HME = await this._loadHME();
      const encoder = await HME.createH264MP4Encoder();

      // MP4 requires even dimensions
      const pw = echartsCanvas.width % 2 === 0 ? echartsCanvas.width : echartsCanvas.width - 1;
      const ph = echartsCanvas.height % 2 === 0 ? echartsCanvas.height : echartsCanvas.height - 1;
      const frameCount = Math.max(2, Math.round(durationMs * fps / 1000));
      const intervalMs = Math.round(durationMs / frameCount);

      encoder.width = pw;
      encoder.height = ph;
      encoder.frameRate = fps;
      encoder.speed = 0;
      encoder.quantizationParameter = 28;
      encoder.groupOfPictures = fps;
      encoder.initialize();

      // Trigger a fresh render to capture layout animation
      this.chartInstance.setOption(this.generateChartOption());

      // Pre-render background layer
      await this._prepareBgLayer(pw, ph);

      // Offscreen canvas for frame capture
      const offscreen = document.createElement('canvas');
      offscreen.width = pw;
      offscreen.height = ph;
      const ctx = offscreen.getContext('2d', { willReadFrequently: true });

      // Capture frames
      for (let i = 0; i < frameCount; i++) {
        await new Promise(resolve => setTimeout(resolve, intervalMs));

        // Draw background layer first, then ECharts canvas on top
        ctx.drawImage(this._bgLayerCanvas, 0, 0);
        ctx.drawImage(echartsCanvas, 0, 0, pw, ph);
        const imageData = ctx.getImageData(0, 0, pw, ph);
        encoder.addFrameRgba(imageData.data);
      }

      // Finalize and get MP4 data
      encoder.finalize();
      const mp4Buffer = encoder.FS.readFile(encoder.outputFilename);
      const mp4Blob = new Blob([mp4Buffer], { type: 'video/mp4' });
      encoder.delete();

      return mp4Blob;
    },

    /**
     * Download a blob as a file
     */
    downloadBlob(blob, filename) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    // ── GIF Export ─────────────────────────────────

    /**
     * Quickly capture the ECharts canvas at its current pixel size.
     * Much faster than html2canvas — just draws the existing canvas to a new one.
     */
    _captureEChartsFrame(pixelWidth, pixelHeight) {
      const echartsCanvas = this.$refs.chart?.querySelector('canvas');
      if (!echartsCanvas) return null;

      const offscreen = document.createElement('canvas');
      offscreen.width = pixelWidth;
      offscreen.height = pixelHeight;
      const ctx = offscreen.getContext('2d');
      // Draw background layer first (from pre-rendered cache)
      if (this._bgLayerCanvas) {
        ctx.drawImage(this._bgLayerCanvas, 0, 0);
      } else {
        ctx.fillStyle = this.backgroundColor || '#ffffff';
        ctx.fillRect(0, 0, pixelWidth, pixelHeight);
      }
      ctx.drawImage(echartsCanvas, 0, 0, pixelWidth, pixelHeight);
      return offscreen;
    },

    /**
     * Capture frames and encode as GIF using modern-gif.
     * Uses direct canvas-to-canvas copy for speed (avoids heavy html2canvas).
     * @returns {Promise<Blob>} GIF blob
     */
    async _prepareBgLayer(pw, ph) {
      const bg = document.createElement('canvas');
      bg.width = pw; bg.height = ph;
      const ctx = bg.getContext('2d');
      ctx.fillStyle = this.backgroundColor || '#ffffff';
      ctx.fillRect(0, 0, pw, ph);
      if (this.bgImageBase64) {
        await new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            const fit = this.bgFitMode || 'cover';
            if (fit === 'stretch') {
              ctx.drawImage(img, 0, 0, pw, ph);
            } else if (fit === 'repeat') {
              const pat = ctx.createPattern(img, 'repeat');
              ctx.fillStyle = pat;
              ctx.fillRect(0, 0, pw, ph);
            } else if (fit === 'contain') {
              const s = Math.min(pw / img.width, ph / img.height);
              const iw = img.width * s, ih = img.height * s;
              ctx.drawImage(img, (pw - iw) / 2, (ph - ih) / 2, iw, ih);
            } else {
              const s = Math.max(pw / img.width, ph / img.height);
              const iw = img.width * s, ih = img.height * s;
              ctx.drawImage(img, (pw - iw) / 2, (ph - ih) / 2, iw, ih);
            }
            if (this.bgImageOpacity !== undefined && this.bgImageOpacity < 1) {
              ctx.globalAlpha = this.bgImageOpacity;
            }
            resolve();
          };
          img.src = this.bgImageBase64;
        });
      }
      this._bgLayerCanvas = bg;
      return bg;
    },

    async exportGif(frameCount = 20, totalDurationMs = 2500) {
      if (!this.$refs.chart || !this.chartInstance) {
        throw new Error('Chart not initialized');
      }

      const chartDom = this.$refs.chart;
      const echartsCanvas = chartDom.querySelector('canvas');
      if (!echartsCanvas) {
        throw new Error('ECharts canvas not found');
      }

      const pw = echartsCanvas.width;
      const ph = echartsCanvas.height;
      const intervalMs = Math.round(totalDurationMs / frameCount);
      const frames = [];

      // Trigger a fresh render to capture layout animation
      this.chartInstance.setOption(this.generateChartOption());

      // Capture frames as the word cloud animates into position
      for (let i = 0; i < frameCount; i++) {
        // Wait for animation to advance
        await new Promise(resolve => setTimeout(resolve, intervalMs));

        const frame = this._captureEChartsFrame(pw, ph);
        if (frame) {
          frames.push(frame);
        }
      }

      if (frames.length < 2) {
        throw new Error('Not enough frames captured');
      }

      // modern-gif: delay in centiseconds (GIF spec), so intervalMs/10
      const delayCs = Math.max(2, Math.round(intervalMs / 10));

      const gifBlob = await encodeGif({
        width: pw,
        height: ph,
        frames: frames.map(canvas => ({
          data: canvas,
          delay: delayCs
        })),
        format: 'blob'
      });

      return gifBlob;
    }
  }
}
</script>

<style scoped>
.chart-container {
  position: relative;
  overflow: hidden;
}

.chart-carousel {
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
}
/* Force carousel internals to fill height */
.chart-carousel :deep(.el-carousel__container),
.chart-carousel :deep(.el-carousel__item),
.chart-carousel :deep(.el-carousel__item) > * {
  height: 100% !important;
}

.chart-bg-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.export-pure {
  padding: 0 !important;
  margin: 0 !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  background: #fff !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  filter: none !important;
  border: none !important;
}

:deep(.el-carousel__arrow) {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
  color: #666 !important;
}

:deep(.el-carousel__arrow):hover {
  background: rgba(255, 255, 255, 0.95);
  color: #333 !important;
}
</style>
